import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import {
  fetchTransferBuckets,
  transferStock,
} from '../../../../../features/stock/stockThunks';
import { clearStockTransfer } from '../../../../../features/stock/stockSlice';
import {
  bucketKey,
  buildTransferPayload,
  transferProblem,
} from '../../../../../features/stock/stockTransferUtils';
import { fetchSlotOptions } from '../../../../../features/slots/slotsThunks';
import { slotOptionLabel } from '../../../../../features/slots/slotsUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

const EMPTY_FORM = { productId: null, fromKey: '', toSlotId: '', quantity: '', notes: '' };

const bucketLabel = (bucket) =>
  [
    bucket.slot_name,
    bucket.lot && `lote ${bucket.lot}`,
    `${formatQuantity(bucket.quantity)} ${bucket.base_uom}`,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Stores or moves stock between two locations of the same warehouse.
 *
 * One act with two faces: moving takes stock already put away to another
 * position, and storing takes it off the receiving dock. The origin is picked
 * among the locations that really hold the product, and the button says what is
 * still missing instead of just sitting there disabled.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.warehouse - The warehouse in use; never "todas".
 * @param {Array<{value: number, label: string}>} props.productOptions - The
 *   products on screen: the ones with stock here, which is what can be moved.
 * @param {object|null} props.initial - The row it was opened from, if any. A
 *   row of the detail also picks the origin.
 * @param {(message: string) => void} props.onDone - Told what happened, in a sentence.
 */
export const TransferStockModal = ({ isOpen, onClose, warehouse, productOptions, initial, onDone }) => {
  const dispatch = useDispatch();
  const { buckets, loadingBuckets, saving } = useSelector((state) => state.stock.transfer);
  const { options: slots, loadingOptions } = useSelector((state) => state.slots);

  const [form, setForm] = useState(EMPTY_FORM);
  const [saveError, setSaveError] = useState(null);

  const warehouseId = warehouse?.id;

  // Every opening starts from the row that asked for it, and closing forgets
  // the origins: the next product must not open on this one's.
  useEffect(() => {
    if (!isOpen) {
      dispatch(clearStockTransfer());
      return;
    }
    setSaveError(null);
    setForm({ ...EMPTY_FORM, productId: initial?.product ?? null });
    if (warehouseId) dispatch(fetchSlotOptions(warehouseId));
  }, [isOpen, initial, warehouseId, dispatch]);

  useEffect(() => {
    if (!isOpen || !form.productId || !warehouseId) return;
    dispatch(fetchTransferBuckets({ productId: form.productId, warehouseId }));
  }, [isOpen, form.productId, warehouseId, dispatch]);

  // Opened from a row of the detail: that location, and that lot, is the origin
  // — for as long as the product is still the one of that row.
  useEffect(() => {
    if (!initial?.slot || buckets.length === 0) return;
    const match = buckets.find(
      (bucket) => bucket.slot === initial.slot && (bucket.lot || '') === (initial.lot || '')
    );
    if (!match) return;
    setForm((prev) =>
      prev.fromKey || prev.productId !== initial.product
        ? prev
        : { ...prev, fromKey: bucketKey(match) }
    );
  }, [initial, buckets]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const from = buckets.find((bucket) => bucketKey(bucket) === form.fromKey) || null;
  const destinations = slots.filter((slot) => !from || slot.id !== from.slot);
  const destination = slots.find((slot) => slot.id === form.toSlotId) || null;
  const product = productOptions.find((option) => option.value === form.productId) || null;

  const draft = { ...form, from };
  const problem = transferProblem(draft);

  const fromHint = (() => {
    if (!form.productId) return 'Primero elige el producto.';
    if (loadingBuckets) return 'Buscando dónde hay...';
    return buckets.length > 0
      ? 'Solo se listan las ubicaciones con stock disponible.'
      : 'Este producto no tiene stock disponible en esta bodega.';
  })();

  const handleSubmit = async () => {
    setSaveError(null);
    const action = await dispatch(transferStock(buildTransferPayload(draft)));

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    onDone(
      `Se movieron ${formatQuantity(form.quantity)} de ${product?.sku || 'el producto'} a ${
        destination?.name || 'su destino'
      }.`
    );
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Guardar o mover stock" testId="transfer-modal">
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {warehouse?.name} · queda registrado en el kardex como traslado
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="transfer-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        <FilterableSelect
          variant="form"
          testId="transfer-product"
          label="Producto"
          value={form.productId}
          // Another product is another set of origins.
          onChange={(productId) => set({ productId, fromKey: '', quantity: '' })}
          options={productOptions}
          placeholder="Seleccionar..."
          searchPlaceholder="Buscar por SKU o nombre..."
          disabled={saving}
        />

        <div>
          <FilterableSelect
            variant="form"
            testId="transfer-from"
            label="Desde"
            value={form.fromKey}
            onChange={(fromKey) => set({ fromKey, quantity: '' })}
            options={buckets.map((bucket) => ({
              value: bucketKey(bucket),
              label: bucketLabel(bucket),
            }))}
            placeholder={form.productId ? 'Seleccionar...' : 'Elige primero un producto'}
            searchPlaceholder="Buscar ubicación o lote..."
            disabled={!form.productId || loadingBuckets || saving}
          />
          <p data-testid="transfer-from-hint" className="text-text-muted text-xs mt-1">
            {fromHint}
          </p>
        </div>

        <div>
          <FilterableSelect
            variant="form"
            testId="transfer-to"
            label="Hacia"
            value={form.toSlotId}
            onChange={(toSlotId) => set({ toSlotId })}
            options={destinations.map((slot) => ({
              value: slot.id,
              label: slotOptionLabel(slot),
            }))}
            placeholder={loadingOptions ? 'Cargando ubicaciones...' : 'Seleccionar...'}
            searchPlaceholder="Buscar por nombre o código..."
            disabled={!from || saving}
          />
          <p className="text-text-muted text-xs mt-1">
            Solo Almacenaje y Picking dejan el stock disponible para vender.
          </p>
        </div>

        <Field
          label="Cantidad"
          htmlFor="transfer-quantity"
          hint={
            from
              ? `Disponible en ${from.slot_name}: ${formatQuantity(from.quantity)} ${from.base_uom}`
              : undefined
          }
        >
          <input
            id="transfer-quantity"
            type="number"
            min="0"
            step="any"
            data-testid="transfer-quantity"
            value={form.quantity}
            onChange={(event) => set({ quantity: event.target.value })}
            disabled={!from || saving}
            className={inputClass}
          />
        </Field>

        <Field
          label="Motivo"
          htmlFor="transfer-notes"
          hint="Opcional, pero quien lea el kardex en seis meses lo va a agradecer."
        >
          <input
            id="transfer-notes"
            type="text"
            data-testid="transfer-notes"
            value={form.notes}
            onChange={(event) => set({ notes: event.target.value })}
            disabled={saving}
            className={inputClass}
          />
        </Field>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing: a dead button with no reason is
            a form somebody gives up on. */}
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Moviendo..."
          disabled={Boolean(problem)}
          onClick={handleSubmit}
          data-testid="transfer-submit"
        >
          {problem || 'Mover'}
        </Button>
      </div>
    </Modal>
  );
};
